import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = logged out
  const [ready, setReady] = useState(false);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const { user } = await api.get("/auth/me");
      setUser(user);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const data = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, invite, username) => {
    const payload = { name, email, password };
    if (invite && invite.trim()) payload.invite = invite.trim();
    if (username && username.trim()) payload.username = username.trim();
    const data = await api.post("/auth/register", payload);
    setToken(data.token);
    setUser(data.user);
    try { if (data.user?.user_id) localStorage.setItem(`ss_onboard:${data.user.user_id}`, "pending"); } catch (e) {}
    return data.user;
  };

  const loginWithGoogle = async (credential, referralCode) => {
    const data = await api.post("/auth/google", { credential, referral_code: referralCode || undefined });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithApple = async (identityToken, name, referralCode) => {
    const data = await api.post("/auth/apple", { identity_token: identityToken, name, referral_code: referralCode || undefined });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    setToken(null);
    setUser(null);
  };

  const refresh = loadMe;

  return (
    <AuthContext.Provider value={{ user, ready, login, loginWithGoogle, loginWithApple, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}
